/**
 * Delete an inventory item — with a reference scan that protects history.
 *
 * Policy (agreed with product): package DEFINITIONS cascade, but deletion is
 * BLOCKED when the item is referenced by financial / clinical / stock history
 * (invoices, dispenses, releases, prescriptions, stock ledger, batches,
 * adjustments, used supplies, procurement lines, or patient-owned packages).
 *
 * The scan runs entirely against the local PowerSync SQLite (offline-first);
 * the cascade uses `db.execute(...)` so PowerSync records CRUD entries and
 * syncs them. Package-composition rows are soft-deleted (deleted_at); the
 * one-to-one detail row, prescription-template lines, and the item itself are
 * hard-deleted — matching each table's existing delete convention.
 */

// PowerSyncDatabase — accepts any object with execute + getAll.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type DbLike = any;

/** A single category of related rows, with its count. */
export interface InventoryRefCount {
  key: string;
  label: string;
  count: number;
}

export interface InventoryDeleteScan {
  itemId: string;
  itemName: string | null;
  isPackage: boolean;
  /** Financial/clinical/stock references — non-empty means deletion is blocked. */
  blockers: InventoryRefCount[];
  /** Related data that WILL be removed if the user proceeds. */
  cascades: InventoryRefCount[];
  canDelete: boolean;
}

async function count(db: DbLike, sql: string, params: unknown[]): Promise<number> {
  const rows = (await db.getAll(sql, params)) as { c: number | null }[];
  return Number(rows[0]?.c ?? 0);
}

/**
 * Scan every table that references an inventory item and bucket the counts into
 * `blockers` (protected history — block deletion) and `cascades` (will be
 * removed). Safe to call repeatedly; performs no writes.
 */
export async function scanInventoryItemReferences(
  db: DbLike,
  itemId: string,
): Promise<InventoryDeleteScan> {
  const itemRows = (await db.getAll(
    `SELECT id, item_name, item_type FROM inventory_items WHERE id = ?`,
    [itemId],
  )) as { id: string; item_name: string | null; item_type: string | null }[];
  const item = itemRows[0];
  const isPackage = (item?.item_type ?? '').toLowerCase() === 'package';

  // ── Blockers: any reference that represents real usage/history/ownership. ──
  const blockerDefs: { key: string; label: string; sql: string; params: unknown[] }[] = [
    {
      key: 'invoice_items',
      label: 'invoice line(s)',
      sql: `SELECT COUNT(*) AS c FROM invoice_items WHERE inventory_item_id = ?`,
      params: [itemId],
    },
    {
      key: 'pharmacy_dispense_lines',
      label: 'pharmacy dispense line(s)',
      sql: `SELECT COUNT(*) AS c FROM pharmacy_dispense_lines WHERE inventory_item_id = ?`,
      params: [itemId],
    },
    {
      key: 'product_release_lines',
      label: 'product release line(s)',
      sql: `SELECT COUNT(*) AS c FROM product_release_lines WHERE inventory_item_id = ?`,
      params: [itemId],
    },
    {
      key: 'patient_prescriptions',
      label: 'patient prescription(s)',
      sql: `SELECT COUNT(*) AS c FROM patient_prescriptions WHERE inventory_item_id = ?`,
      params: [itemId],
    },
    {
      key: 'stock_movements',
      label: 'stock movement(s)',
      sql: `SELECT COUNT(*) AS c FROM stock_movements WHERE inventory_item_id = ?`,
      params: [itemId],
    },
    {
      key: 'inventory_batches',
      label: 'stock batch(es)',
      sql: `SELECT COUNT(*) AS c FROM inventory_batches WHERE inventory_item_id = ?`,
      params: [itemId],
    },
    {
      key: 'inventory_adjustment_lines',
      label: 'inventory adjustment line(s)',
      sql: `SELECT COUNT(*) AS c FROM inventory_adjustment_lines WHERE inventory_item_id = ?`,
      params: [itemId],
    },
    {
      key: 'used_supplies',
      label: 'used-supply record(s)',
      sql: `SELECT COUNT(*) AS c FROM used_supplies WHERE inventory_item_id = ? AND deleted_at IS NULL`,
      params: [itemId],
    },
    {
      key: 'delivery_order_lines',
      label: 'delivery order line(s)',
      sql: `SELECT COUNT(*) AS c FROM delivery_order_lines WHERE inventory_item_id = ?`,
      params: [itemId],
    },
    {
      key: 'purchase_order_lines',
      label: 'purchase order line(s)',
      sql: `SELECT COUNT(*) AS c FROM purchase_order_lines WHERE inventory_item_id = ?`,
      params: [itemId],
    },
    {
      key: 'patient_packages',
      label: 'patient-owned package(s)',
      sql: `SELECT COUNT(*) AS c FROM patient_packages WHERE package_inventory_item_id = ? AND deleted_at IS NULL`,
      params: [itemId],
    },
    {
      key: 'patient_package_items',
      label: 'patient package benefit(s)',
      sql: `SELECT COUNT(*) AS c FROM patient_package_items WHERE child_inventory_item_id = ? AND deleted_at IS NULL`,
      params: [itemId],
    },
    {
      key: 'patient_package_redemptions',
      label: 'package redemption(s)',
      sql: `SELECT COUNT(*) AS c FROM patient_package_redemptions WHERE child_inventory_item_id = ? AND deleted_at IS NULL`,
      params: [itemId],
    },
  ];

  const blockers: InventoryRefCount[] = [];
  for (const d of blockerDefs) {
    const c = await count(db, d.sql, d.params);
    if (c > 0) blockers.push({ key: d.key, label: d.label, count: c });
  }

  // ── Cascades: definitions/config that will be removed on delete. ──
  const cascades: InventoryRefCount[] = [];
  const inPackages = await count(
    db,
    `SELECT COUNT(*) AS c FROM inventory_package_items
       WHERE child_inventory_item_id = ? AND deleted_at IS NULL`,
    [itemId],
  );
  if (inPackages > 0) {
    cascades.push({
      key: 'package_membership',
      label: 'package(s) that include this item',
      count: inPackages,
    });
  }
  if (isPackage) {
    const composition = await count(
      db,
      `SELECT COUNT(*) AS c FROM inventory_package_items
         WHERE package_inventory_item_id = ? AND deleted_at IS NULL`,
      [itemId],
    );
    if (composition > 0) {
      cascades.push({
        key: 'package_composition',
        label: 'included item(s) in this package',
        count: composition,
      });
    }
  }
  const templateLines = await count(
    db,
    `SELECT COUNT(*) AS c FROM prescription_template_items WHERE inventory_item_id = ?`,
    [itemId],
  );
  if (templateLines > 0) {
    cascades.push({
      key: 'prescription_template_items',
      label: 'prescription template line(s)',
      count: templateLines,
    });
  }

  return {
    itemId,
    itemName: item?.item_name ?? null,
    isPackage,
    blockers,
    cascades,
    canDelete: Boolean(item) && blockers.length === 0,
  };
}

export interface DeleteInventoryItemResult {
  deleted: boolean;
  error?: string;
}

/**
 * Delete the inventory item and cascade its package definitions / templates.
 * Re-scans first and refuses if any blocker exists (guards against a stale UI).
 */
export async function deleteInventoryItemCascade(
  db: DbLike,
  itemId: string,
): Promise<DeleteInventoryItemResult> {
  const scan = await scanInventoryItemReferences(db, itemId);
  if (!scan.itemName && scan.blockers.length === 0 && scan.cascades.length === 0) {
    // Item row missing → nothing to do (treat as already deleted).
    const stillExists = (await db.getAll(`SELECT id FROM inventory_items WHERE id = ?`, [
      itemId,
    ])) as {
      id: string;
    }[];
    if (stillExists.length === 0) return { deleted: true };
  }
  if (!scan.canDelete) {
    return {
      deleted: false,
      error: 'This item has related records that must be resolved before it can be deleted.',
    };
  }

  const now = new Date().toISOString();

  // 1. Soft-delete package-composition rows referencing this item (as a child
  //    of other packages, and — if it is a package — its own included items).
  await db.execute(
    `UPDATE inventory_package_items SET deleted_at = ?, updated_at = ?
       WHERE (child_inventory_item_id = ? OR package_inventory_item_id = ?)
         AND deleted_at IS NULL`,
    [now, now, itemId, itemId],
  );

  // 2. Hard-delete prescription-template lines (config, no soft-delete column).
  await db.execute(`DELETE FROM prescription_template_items WHERE inventory_item_id = ?`, [itemId]);

  // 3. Hard-delete the one-to-one medicine/administered-detail row.
  await db.execute(`DELETE FROM inventory_medicine_details WHERE inventory_item_id = ?`, [itemId]);

  // 4. Hard-delete the item itself.
  await db.execute(`DELETE FROM inventory_items WHERE id = ?`, [itemId]);

  return { deleted: true };
}
